import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

import postOrder from '@/common/api/orders/postOrder';
import Snack from '@/common/components/molecules/Snack/Snack';

const useOrder = ({ disabled, isLoading }) => {
  const { t } = useTranslation();

  const {
    mutate: mutatePostOrder,
    isLoading: isLoadingPostOrder,
    isSuccess: isSuccessPostOrder,
  } = useMutation(postOrder);
  /**
   * Add an order from a Checkout
   * @params {object} values - order values
   * @params {object} actions - formik actions
   * @params {function} onSuccess - on success callback
   */
  const onAddOrder = useCallback(
    ({ values, actions, onSuccess = () => {} }) => {
      mutatePostOrder(values, {
        onSuccess: (data) => {
          if (data.acknowledged || values.sync === 1) {
            onSuccess(data);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          } else {
            toast.error(
              <Snack message={t('common:messages:forms.errors.default')} />,
            );
          }
        },
        onError: (error) => {
          if (error.response.status === 500) {
            toast.error(
              <Snack
                message={t('common:messages:forms.errors.server_error')}
              />,
            );
          } else {
            if (error.response.data.errors) {
              actions.setErrors(error.response.data.errors);
            }

            toast.error(
              <Snack message={t('common:messages:forms.errors.default')} />,
            );
          }
        },
      });
    },
    [mutatePostOrder, t],
  );

  /**
   * Check if action is disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(() => {
    const loading = isLoading || isLoadingPostOrder;

    return {
      loading,
      disabled: loading || disabled,
    };
  }, [disabled, isLoading, isLoadingPostOrder]);

  return {
    isDisabled,
    isSuccess: isSuccessPostOrder,
    onAddOrder,
  };
};

export default useOrder;
